import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ButtonControl, DialogControl } from "spark-core-dx/components";
import { AI_PANEL_GRID_REFRESH_EVENT } from "../../../../../components/AIChat/aiPanelCommand";
import { Operations } from "../../../../../services/ItemServices";
import { useResourceDomainContext } from "../../../ResourceDomainProvider";
import MarketplaceAllowancePurchaseDialog from "../../../../marketplace/components/MarketplaceAllowancePurchaseDialog";
import { getProduct, normalizeMarketplaceCatalog } from "../../../../marketplace/data/marketplaceCatalog";
import {
  buildGrantCheckSummary,
  buildResourceOperationQueryParams,
  buildWizardValidationError,
  formatCount,
  getOperationItem,
  getId,
  getValue,
  requireSuccessfulServiceResponse
} from "../resourceConfigWizardUtils";
import { InfoPair } from "./WizardStepCommon";

const PRODUCT_MARKETPLACE_CATALOG_ITEM = "ProductMarketplaceCatalog";

const copyTextToClipboard = async (text) => {
  if (!text) return;
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const textArea = document.createElement("textarea");
  textArea.value = text;
  textArea.setAttribute("readonly", "");
  textArea.style.position = "fixed";
  textArea.style.left = "-9999px";
  document.body.appendChild(textArea);
  textArea.select();
  document.execCommand("copy");
  document.body.removeChild(textArea);
};

const getGrantSelectionKey = (grant) => grant
  ? `${grant.clientProductOfferId || ""}:${String(grant.productResourceKey || "").toLowerCase()}`
  : "";

const grantsEqual = (left, right) =>
  Boolean(left && right && getGrantSelectionKey(left) === getGrantSelectionKey(right));

const grantConsumesCurrentConfiguration = (grant, appliedGrant) =>
  Boolean(grant && appliedGrant && (grant.id === appliedGrant.id || grantsEqual(grant, appliedGrant)));

const getGrantAppliedCount = (grant, appliedGrant) =>
  Math.max(0, Number(grant?.assigned) || 0) + (grantConsumesCurrentConfiguration(grant, appliedGrant) ? 1 : 0);

const getGrantRemainingCount = (grant, appliedCount) => {
  if (grant?.unlimited) return Number.POSITIVE_INFINITY;
  const allowed = Number(grant?.allowed);
  if (!Number.isFinite(allowed)) return Number.POSITIVE_INFINITY;
  return Math.max(0, allowed - appliedCount);
};

const isTruthy = (value) => value === true || String(value || "").toLowerCase() === "true";

const findMarketplaceResource = (catalog, resourceKey) => {
  const key = String(resourceKey || "").toLowerCase();
  return (catalog?.resources || []).find((resource) =>
    String(resource.key || "").toLowerCase() === key
    || String(resource.id || resource._id || "").toLowerCase() === key) || null;
};

const GrantCheckStep = ({ context, onSave, onWizardRefresh, registerStepController }) => {
  const resourceDomainContext = useResourceDomainContext();
  const coreState = resourceDomainContext?.coreState;
  const marketplaceCatalogMgr = resourceDomainContext?.managers?.productMarketplaceCatalog;
  const { wizard, values, readOnly, saving } = context;
  const summary = useMemo(() => buildGrantCheckSummary(wizard), [wizard]);
  const [copiedGrantCode, setCopiedGrantCode] = useState("");
  const [pendingGrantKey, setPendingGrantKey] = useState("");
  const [marketplaceCatalog, setMarketplaceCatalog] = useState(null);
  const [marketplaceCatalogLoading, setMarketplaceCatalogLoading] = useState(false);
  const [marketplaceCatalogError, setMarketplaceCatalogError] = useState("");
  const [purchaseDialogOpen, setPurchaseDialogOpen] = useState(false);
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);
  const [clearAppliedGrantRequested, setClearAppliedGrantRequested] = useState(false);
  const autoAppliedGrantRef = useRef("");
  const canContinueRef = useRef(() => false);
  const prepareAllowanceValuesRef = useRef(null);
  const buildAppliedAllowanceValuesRef = useRef(null);
  const hasGrantDataRef = useRef(false);
  const clientResourceId = getValue(wizard, "ClientResourceId", "clientResourceId") || getId(summary.clientResource);
  const resourceConfigurationId = getValue(wizard, "ResourceConfigurationId", "resourceConfigurationId") || getId(summary.resourceConfiguration);
  const allowanceClearStored = isTruthy(values?.ClearAllowanceGrant) && !summary.hasAppliedGrant;
  const pendingGrant = useMemo(
    () => summary.grants.find((grant) => getGrantSelectionKey(grant) === pendingGrantKey) || null,
    [pendingGrantKey, summary.grants]
  );
  const suppressDefaultGrant = allowanceClearStored && !pendingGrant;
  const applyCandidateGrant = pendingGrant || (!suppressDefaultGrant && !summary.hasAppliedGrant ? summary.defaultGrant : null);
  const continueCandidateGrant = summary.appliedGrant || applyCandidateGrant;
  const allowanceClearedWithoutSelection = clearAppliedGrantRequested
    || (allowanceClearStored && !pendingGrant && !summary.appliedGrant);
  const canContinueGrantCheck = Boolean(resourceConfigurationId)
    && !allowanceClearedWithoutSelection
    && (summary.hasGrantData ? Boolean(continueCandidateGrant) : summary.remaining > 0);
  const visibleSelectedGrant = pendingGrant || summary.appliedGrant || (!suppressDefaultGrant ? summary.defaultGrant : null);
  const hasPendingApply = summary.hasGrantData && Boolean(applyCandidateGrant) && !grantsEqual(applyCandidateGrant, summary.appliedGrant);
  const needsAllowanceApply = summary.hasGrantData && !summary.hasAppliedGrant;
  const showBuy = summary.hasGrantData ? summary.availableGrants.length === 0 : summary.remaining <= 0;
  const otherUnattributedCount = Math.max(0, summary.unattributedCount - (summary.currentConfigurationIsUnattributed ? 1 : 0));
  const marketplaceResource = useMemo(
    () => findMarketplaceResource(marketplaceCatalog, summary.productResourceKey),
    [marketplaceCatalog, summary.productResourceKey]
  );
  const marketplaceProduct = useMemo(
    () => getProduct(marketplaceResource?.productKey, marketplaceCatalog?.products || []),
    [marketplaceCatalog?.products, marketplaceResource?.productKey]
  );
  const grantStatusText = canContinueGrantCheck
    ? "Allowance Approved"
    : applyCandidateGrant
      ? "Apply Grant"
      : showBuy
        ? "No allowance available"
        : "Select Allowance";
  const summaryMetricGrant = summary.appliedGrant;
  const summaryAppliedCount = summaryMetricGrant
    ? getGrantAppliedCount(summaryMetricGrant, summary.appliedGrant)
    : 0;
  const summaryRemainingCount = summaryMetricGrant
    ? getGrantRemainingCount(summaryMetricGrant, summaryAppliedCount)
    : 0;
  const summaryAvailableCount = summaryMetricGrant?.allowed ?? 0;

  const selectGrant = useCallback((grant) => {
    if (!grant || readOnly) return;
    setPendingGrantKey(grantsEqual(grant, summary.appliedGrant) ? "" : getGrantSelectionKey(grant));
  }, [readOnly, summary.appliedGrant]);

  const requestClearAppliedGrant = useCallback(() => {
    if (!summary.appliedGrant || readOnly || saving) return;
    setClearConfirmOpen(true);
  }, [readOnly, saving, summary.appliedGrant]);

  const confirmClearAppliedGrant = useCallback(() => {
    setClearConfirmOpen(false);
    setPendingGrantKey("");
    setClearAppliedGrantRequested(true);
  }, []);

  useEffect(() => {
    if (!pendingGrantKey || !summary.appliedGrant) return;
    if (getGrantSelectionKey(summary.appliedGrant) === pendingGrantKey) {
      setPendingGrantKey("");
    }
  }, [pendingGrantKey, summary.appliedGrant]);

  useEffect(() => {
    if (!copiedGrantCode) return undefined;
    const timeoutId = window.setTimeout(() => setCopiedGrantCode(""), 1500);
    return () => window.clearTimeout(timeoutId);
  }, [copiedGrantCode]);

  const loadMarketplaceCatalog = useCallback(async () => {
    if (marketplaceCatalog) return marketplaceCatalog;
    setMarketplaceCatalogLoading(true);
    setMarketplaceCatalogError("");

    try {
      if (!marketplaceCatalogMgr) {
        throw new Error("Marketplace catalog service is not available.");
      }
      const response = await marketplaceCatalogMgr.RunOperation({
        operationName: Operations.ProductMarketplaceCatalogItem.LoadProductMarketplaceCatalog,
        item: { ItemName: PRODUCT_MARKETPLACE_CATALOG_ITEM },
        queryParams: buildResourceOperationQueryParams({ wizard }),
        isItem: true,
        isWarning: true,
        supressAllErrors: false
      });
      requireSuccessfulServiceResponse(response, "Marketplace catalog could not be loaded.", coreState);
      const catalogItem = getOperationItem(response);
      if (!catalogItem) {
        throw new Error("Marketplace catalog could not be loaded.");
      }
      const normalizedCatalog = normalizeMarketplaceCatalog(catalogItem);
      setMarketplaceCatalog(normalizedCatalog);
      return normalizedCatalog;
    } catch (error) {
      setMarketplaceCatalogError(error?.message || "Marketplace catalog could not be loaded.");
      return null;
    } finally {
      setMarketplaceCatalogLoading(false);
    }
  }, [coreState, marketplaceCatalog, marketplaceCatalogMgr, wizard]);

  const buildAppliedAllowanceValues = useCallback((currentValues) => {
    if (!summary.hasGrantData) return currentValues;
    const grant = continueCandidateGrant;
    if (!grant) {
      throw buildWizardValidationError("Select an allowance before continuing.");
    }
    return {
      ...currentValues,
      ClearAllowanceGrant: false,
      AllowanceClientProductOfferId: grant.clientProductOfferId,
      AllowanceProductOfferKey: grant.productOfferKey,
      AllowanceProductOfferSku: grant.productOfferSku,
      AllowanceGrantProductResourceKey: grant.productResourceKey
    };
  }, [continueCandidateGrant, summary.hasGrantData]);

  const buildClearedAllowanceValues = useCallback((currentValues) => ({
    ...currentValues,
    ClearAllowanceGrant: true,
    AllowanceClientProductOfferId: "",
    AllowanceProductOfferKey: "",
    AllowanceProductOfferSku: "",
    AllowanceGrantProductResourceKey: ""
  }), []);

  const prepareAllowanceValues = useCallback((currentValues) => {
    if (clearAppliedGrantRequested || (allowanceClearStored && !pendingGrant && !summary.appliedGrant)) {
      return buildClearedAllowanceValues(currentValues);
    }
    const grant = applyCandidateGrant || summary.appliedGrant || summary.defaultGrant;
    if (!resourceConfigurationId) {
      throw buildWizardValidationError("No usable allowance is available. Select or activate a matching grant before applying this configuration.");
    }
    if (summary.hasGrantData && !grant) {
      throw buildWizardValidationError(summary.availableGrants.length > 1
        ? "Select which grant this configuration should consume before applying it."
        : "No usable matching grant has remaining capacity.");
    }
    if (!summary.hasGrantData && summary.remaining <= 0) {
      throw buildWizardValidationError("No usable resource allowance is available.");
    }

    return summary.hasGrantData
      ? {
          ...currentValues,
          ClearAllowanceGrant: false,
          AllowanceClientProductOfferId: grant.clientProductOfferId,
          AllowanceProductOfferKey: grant.productOfferKey,
          AllowanceProductOfferSku: grant.productOfferSku,
          AllowanceGrantProductResourceKey: grant.productResourceKey
        }
      : currentValues;
  }, [allowanceClearStored, applyCandidateGrant, buildClearedAllowanceValues, clearAppliedGrantRequested, pendingGrant, resourceConfigurationId, summary]);

  canContinueRef.current = () => canContinueGrantCheck;
  prepareAllowanceValuesRef.current = prepareAllowanceValues;
  buildAppliedAllowanceValuesRef.current = buildAppliedAllowanceValues;
  hasGrantDataRef.current = summary.hasGrantData;

  useEffect(() => {
    registerStepController?.({
      skipStepInputValidation: true,
      canContinue: () => canContinueRef.current?.() ?? true,
      prepareSave: async (currentValues) =>
        prepareAllowanceValuesRef.current?.(currentValues) ?? currentValues,
      prepareContinue: async (currentValues) => {
        if (hasGrantDataRef.current && !canContinueRef.current?.()) {
          throw buildWizardValidationError("Select an allowance before continuing.");
        }
        return buildAppliedAllowanceValuesRef.current?.(currentValues) ?? currentValues;
      }
    });
  }, [buildAppliedAllowanceValues, canContinueGrantCheck, prepareAllowanceValues, registerStepController, summary.hasGrantData]);

  useEffect(() => {
    return () => registerStepController?.(null);
  }, [registerStepController]);

  useEffect(() => {
    if (!clearAppliedGrantRequested) return undefined;
    let cancelled = false;
    const saveClear = async () => {
      try {
        await onSave?.();
      } catch (error) {
        console.error("Failed to remove applied grant.", error);
      } finally {
        if (!cancelled) {
          setClearAppliedGrantRequested(false);
        }
      }
    };
    void saveClear();
    return () => {
      cancelled = true;
    };
  }, [clearAppliedGrantRequested, onSave]);

  useEffect(() => {
    const autoGrant = summary.defaultGrant;
    const autoGrantKey = autoGrant
      ? `${autoGrant.clientProductOfferId || ""}:${autoGrant.productOfferKey || ""}:${autoGrant.productResourceKey || ""}`
      : "";
    if (readOnly || saving || allowanceClearStored || !autoGrant || summary.hasAppliedGrant || autoAppliedGrantRef.current === autoGrantKey) return;
    autoAppliedGrantRef.current = autoGrantKey;
    setPendingGrantKey(getGrantSelectionKey(autoGrant));
    const timeoutId = window.setTimeout(() => {
      Promise.resolve(onSave?.()).catch((error) => {
        console.error("Failed to auto-apply default grant.", error);
      });
    }, 0);
    return () => window.clearTimeout(timeoutId);
  }, [allowanceClearStored, onSave, readOnly, saving, summary.defaultGrant, summary.hasAppliedGrant]);

  const handleAllowanceAction = async () => {
    const catalog = await loadMarketplaceCatalog();
    if (!catalog) return;
    const resource = findMarketplaceResource(catalog, summary.productResourceKey);
    if (!resource) {
      setMarketplaceCatalogError("Marketplace resource was not found.");
      return;
    }
    setPurchaseDialogOpen(true);
  };

  const handlePurchased = useCallback(() => {
    setPurchaseDialogOpen(false);
    setMarketplaceCatalog(null);
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent(AI_PANEL_GRID_REFRESH_EVENT, {
        cancelable: true,
        detail: {
          panel: "marketplaceaddresource",
          itemName: "ResourceConfigurationWizard",
          productResourceKey: summary.productResourceKey,
          clientResourceId,
          resourceConfigurationId,
          reason: "purchase"
        }
      }));
    }
    onWizardRefresh?.("purchase");
  }, [clientResourceId, onWizardRefresh, resourceConfigurationId, summary.productResourceKey]);

  const handleCopyGrantCode = useCallback(async (grantCode) => {
    try {
      await copyTextToClipboard(grantCode);
      setCopiedGrantCode(grantCode);
    } catch (error) {
      console.error("Failed to copy grant code.", error);
      window.alert?.("Grant code could not be copied.");
    }
  }, []);

  return (
    <div className="resource-wizard__grant-check">
      <section className="resource-wizard__grant-summary">
        <div className="resource-wizard__grant-summary-header">
          <div>
            <span className="resource-wizard__eyebrow">This Client Resource</span>
            <h2>{getValue(wizard, "ResourceName", "resourceName") || summary.productResourceKey}</h2>
          </div>
          <div className="resource-wizard__grant-summary-actions">
            <div className={`resource-wizard__grant-status${canContinueGrantCheck ? " ready" : " blocked"}`}>
              {grantStatusText}
            </div>
          </div>
        </div>
        <div className="resource-wizard__grant-metrics">
          <InfoPair label="Available" value={formatCount(summaryAvailableCount)} />
          <InfoPair label="Applied" value={formatCount(summaryAppliedCount)} />
          <InfoPair label="Remaining" value={formatCount(summaryRemainingCount)} />
          <div className="resource-wizard__info-pair resource-wizard__applied-grant-pair">
            <span>Applied Grant</span>
            <strong>
              {summary.appliedGrant?.name || (summary.usesLegacyFallback ? "Legacy resource allowance" : "Unassigned")}
              {summary.appliedGrant && !readOnly ? (
                <button
                  type="button"
                  className="resource-wizard__grant-clear"
                  aria-label="Remove applied grant"
                  title="Remove applied grant"
                  onClick={requestClearAppliedGrant}
                  disabled={saving || clearAppliedGrantRequested}
                >
                  <span className="material-symbols-rounded" aria-hidden="true">delete</span>
                </button>
              ) : null}
            </strong>
          </div>
        </div>
      </section>

      <section className="resource-wizard__grant-breakdown">
        <div className="resource-wizard__grant-breakdown-header">
            <div className="resource-wizard__grant-breakdown-title">
              <h3>Available Grants</h3>
              <p>{marketplaceCatalog?.isSelfHostedResourceMode
                ? "Select the available Self-Hosted grant below. Next will apply it when you continue."
                : "Select a purchased grant below. Next will apply it when you continue."}</p>
            </div>
          <div className="resource-wizard__grant-actions">
            {!readOnly && hasPendingApply ? (
              <ButtonControl
                type="ok-small"
                className="resource-wizard__button resource-wizard__button--primary"
                onClick={onSave}
                disabled={saving}
                loading={saving}
              >
                Apply
              </ButtonControl>
            ) : null}
            {(!readOnly && summary.hasGrantData) || showBuy ? (
              <ButtonControl
                type="ok-small"
                className="resource-wizard__button resource-wizard__button--primary"
                onClick={handleAllowanceAction}
                disabled={marketplaceCatalogLoading}
                loading={marketplaceCatalogLoading}
              >
                {marketplaceCatalog?.isSelfHostedResourceMode ? "Select" : "Buy"}
              </ButtonControl>
            ) : null}
          </div>
        </div>

        {summary.grants.length > 0 ? (
          <div className="resource-wizard__grant-list">
            {summary.grants.map((grant) => {
              const selected = visibleSelectedGrant?.id === grant.id;
              const disabled = readOnly || (!grant.unlimited && grant.remaining <= 0 && !selected);
              const grantCode = grant.productOfferSku || grant.productOfferKey || "";
              const copied = copiedGrantCode === grantCode;
              const appliedCount = getGrantAppliedCount(grant, summary.appliedGrant);
              const remainingCount = getGrantRemainingCount(grant, appliedCount);
              return (
                <div
                  key={grant.id}
                  className={`resource-wizard__grant-card-shell${selected ? " selected" : ""}${disabled ? " disabled" : ""}`}
                >
                  <button
                    type="button"
                    className="resource-wizard__grant-card"
                    disabled={disabled}
                    onClick={() => selectGrant(grant)}
                  >
                    <span>{grant.name}</span>
                    <small>Resource: {grant.productResourceKey}</small>
                    <div className="resource-wizard__grant-card-metrics">
                      <InfoPair label="Qty" value={formatCount(grant.quantity)} />
                      <InfoPair label="Available" value={formatCount(grant.allowed)} />
                      <InfoPair label="Applied" value={formatCount(appliedCount)} />
                      <InfoPair label="Remaining" value={formatCount(remainingCount)} />
                    </div>
                  </button>
                  {grantCode ? (
                    <div className="resource-wizard__grant-card-code-row">
                      <strong title={grantCode}>{grantCode}</strong>
                      <button
                        type="button"
                        className="resource-wizard__grant-copy"
                        aria-label={`Copy grant code ${grantCode}`}
                        title={copied ? "Copied" : `Copy ${grantCode}`}
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          void handleCopyGrantCode(grantCode);
                        }}
                      >
                        <span className="material-symbols-rounded" aria-hidden="true">{copied ? "check" : "content_copy"}</span>
                      </button>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="resource-wizard__grant-empty">
            <p>No active product-offer grant is assigned to this resource. Legacy allowance is used only when no active grant data exists.</p>
          </div>
        )}

        {hasPendingApply || needsAllowanceApply ? (
          <p className="resource-wizard__grant-note">
            {hasPendingApply && applyCandidateGrant && summary.appliedGrant
              ? `Selected ${applyCandidateGrant.name}. Choose Apply to change this configuration from ${summary.appliedGrant.name} to ${applyCandidateGrant.name}.`
              : applyCandidateGrant
                ? `Selected ${applyCandidateGrant.name}. Continue to apply it, or choose Apply to save it now.`
                : "Select an allowance before continuing."}
          </p>
        ) : null}
        {otherUnattributedCount > 0 ? (
          <p className="resource-wizard__grant-note">{otherUnattributedCount} other configuration(s) are not assigned to an allowance yet and still count against usage.</p>
        ) : null}
        {marketplaceCatalogError ? (
          <p className="resource-wizard__grant-note resource-wizard__grant-note--error">{marketplaceCatalogError}</p>
        ) : null}
      </section>
      <MarketplaceAllowancePurchaseDialog
        open={purchaseDialogOpen}
        resource={marketplaceResource}
        product={marketplaceProduct}
        environmentKey={marketplaceCatalog?.environmentKey || ""}
        clientProductOffers={marketplaceCatalog?.clientProductOffers || []}
        initialOfferKey=""
        isSelfHostedResourceMode={marketplaceCatalog?.isSelfHostedResourceMode}
        beforeCheckout={onSave}
        onClose={() => setPurchaseDialogOpen(false)}
        onPurchased={handlePurchased}
      />
      <DialogControl
        title="Remove Applied Grant"
        openDialog={clearConfirmOpen}
        okText="Remove Grant"
        cancelText="Keep Grant"
        onOk={confirmClearAppliedGrant}
        onCancel={() => setClearConfirmOpen(false)}
        onX={() => setClearConfirmOpen(false)}
      >
        <p>This will remove the applied grant from this configuration. You can stay on Grant Check, but you cannot continue until a grant is applied again.</p>
      </DialogControl>
    </div>
  );
};

export default GrantCheckStep;
